import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import { useShowWidgetAfterChannelJoin } from '@liveHelper/hooks/useShowWidgetAfterChannelJoin'
import {
  actionsSelector,
  automatedMessageDismissTimeSelector,
  automatedMessageSelector,
  conversationSelector,
  isWidgetVisibleSelector,
  previousConversationSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import type { AutomatedMessage } from '@liveHelper/models/store/libs/actions'

import { usePrevious } from '@core/hooks/usePrevious'

export type AutomatedMessageContextProps = {
  automatedMessage: AutomatedMessage | undefined
}

export const AutomatedMessageContext =
  createContext<AutomatedMessageContextProps>({
    automatedMessage: undefined,
  })

/** Provider component for automated message */
export const AutomatedMessageProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const automatedMessageResponse = useStore(automatedMessageSelector)
  const conversation = useStore(conversationSelector)
  const previousConversation = useStore(previousConversationSelector)
  const automatedMessageDismissTime = useStore(
    automatedMessageDismissTimeSelector,
  )
  const { clearAutomatedMessageDelay } = useStore(actionsSelector)
  const uiState = useStore(uiStateSelector)
  const isWidgetVisible = useStore(isWidgetVisibleSelector)

  const previousUIState = usePrevious(uiState)
  const isOffline = useIsOffline()
  const chatChannelConfig = useChatChannelConfig()

  const dismissedInLast24Hours =
    automatedMessageDismissTime &&
    // eslint-disable-next-line react-hooks/purity -- pre-existing impure call in render; behavior-preserving fix deferred
    automatedMessageDismissTime > Date.now() - 24 * 60 * 60 * 1000

  const [automatedMessage, setAutomatedMessage] = useState<
    AutomatedMessage | undefined
  >(undefined)

  const [showMessage, setShowMessage] = useState<boolean>(true)

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    const displayAutomatedMessge =
      automatedMessageResponse &&
      !isOffline &&
      automatedMessageResponse.src === 'chat_channel'

    if (
      !automatedMessage &&
      !conversation &&
      !previousConversation &&
      !dismissedInLast24Hours &&
      displayAutomatedMessge
    ) {
      timeout = setTimeout(() => {
        setAutomatedMessage(automatedMessageResponse)
        // we clear automated message delay, after it's visible it should be always
        // instantly visible for cases like page refresh or when conversation was not started
        clearAutomatedMessageDelay()
      }, automatedMessageResponse.delay)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [
    automatedMessage,
    automatedMessageResponse,
    conversation,
    previousConversation,
    dismissedInLast24Hours,
    clearAutomatedMessageDelay,
    isOffline,
  ])

  // cleanup of context after message cleared from store
  useEffect(() => {
    if (automatedMessage && !automatedMessageResponse) {
      setAutomatedMessage(undefined)
    }
  }, [automatedMessage, automatedMessageResponse])

  const showWidgetAfterChannelJoin = useShowWidgetAfterChannelJoin()

  useEffect(() => {
    // For Chat channel, widget should be hidden due to qualify visitors upon handleJoinSuccessReply
    // but if automated message is returned, we should show the widget immediately
    // needs to check in interval because we need to trigger this after handleJoinSuccessReply
    const interval = setInterval(() => {
      if (
        !isWidgetVisible &&
        Boolean(automatedMessageResponse) &&
        chatChannelConfig?.feature_flags?.qualify_visitor &&
        chatChannelConfig?.config?.restrict_to_qualified_visitors &&
        automatedMessageResponse?.src === 'chat_channel'
      ) {
        showWidgetAfterChannelJoin()
        clearInterval(interval)
      }
    }, 250)

    const timeout = setTimeout(
      () => {
        clearInterval(interval)
      },
      (automatedMessageResponse?.delay ?? 0) + 5000,
    )

    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [
    automatedMessage,
    automatedMessageResponse,
    automatedMessageResponse?.delay,
    chatChannelConfig?.config?.restrict_to_qualified_visitors,
    chatChannelConfig?.feature_flags?.qualify_visitor,
    isWidgetVisible,
    showWidgetAfterChannelJoin,
  ])

  useEffect(() => {
    // if user interacted with widget in any way,
    // we should hide the message if it was already visible
    // for this page load, it should be visible on next page again if not dismissed
    if (
      showMessage &&
      automatedMessage &&
      uiState !== 'idle_minimized' &&
      previousUIState === 'idle_minimized'
    ) {
      setShowMessage(false)
    }
  }, [uiState, previousUIState, automatedMessage, showMessage])

  return (
    <AutomatedMessageContext.Provider
      value={{
        automatedMessage:
          showMessage && automatedMessage ? automatedMessage : undefined,
      }}
    >
      {children}
    </AutomatedMessageContext.Provider>
  )
}

/** @returns AutomatedMessageContext */
export const useAutomatedMessageContext = () => {
  return useContext(AutomatedMessageContext)
}
